<template>
  <div class="tn-booking">
    <template v-if="loading">
      <v-skeleton-loader type="card-heading, list-item-two-line" class="mb-6"></v-skeleton-loader>
      <div class="tn-booking__grid">
        <v-skeleton-loader type="card, card"></v-skeleton-loader>
        <v-skeleton-loader type="card"></v-skeleton-loader>
      </div>
    </template>

    <v-card v-else-if="!booking" class="tn-empty py-12">
      <v-icon size="44">mdi-file-alert-outline</v-icon>
      <div>Couldn't get the booking details.</div>
      <v-btn text color="primary" :to="{ name: 'adventuresBookings' }">Back to adventure bookings</v-btn>
    </v-card>

    <template v-else>
      <v-alert v-if="!booking.activity_details" type="info" text dense class="mb-5">
        Tour dates, pickup, the customer's account and the payment show up once the latest API update is deployed.
      </v-alert>

      <booking-summary
        :booking="booking"
        type="adventures"
        icon="mdi-compass-outline"
        :title="title"
        :tags="tags"
        :total="booking.total_price"
      />

      <div class="tn-booking__grid">
        <div class="tn-booking__col">
          <v-card class="tn-panel">
            <div class="tn-panel__head"><v-icon size="20">mdi-hiking</v-icon> Tour</div>
            <div class="tn-panel__body">
              <dl class="tn-facts">
                <div><dt>Tour date</dt><dd>{{ tourDateLabel }}</dd></div>
                <div><dt>Guests</dt><dd>{{ guestsLabel }}</dd></div>
                <div><dt>Price per adult</dt><dd>{{ details.price_per_person ? money(details.price_per_person) : '—' }}</dd></div>
                <div><dt>Activities</dt><dd>{{ details.activities.length }}</dd></div>
              </dl>

              <div v-for="(activity, i) in details.activities" :key="i" class="tn-activity">
                <span class="tn-activity__thumb">
                  <v-icon size="26">mdi-image-outline</v-icon>
                  <img v-if="activity.image" :src="activity.image" alt="" loading="lazy" @error="$event.target.style.display = 'none'">
                </span>
                <div class="tn-activity__body">
                  <div class="tn-activity__title">
                    {{ activity.title || 'Activity #' + activity.id }}
                    <span v-if="activity.deleted" class="tn-tag">Removed from the catalog</span>
                  </div>
                  <div class="tn-activity__meta">
                    <span v-if="activity.date"><v-icon size="16">mdi-calendar-blank-outline</v-icon>{{ weekday(activity.date) }}, {{ date(activity.date) }}</span>
                    <span v-if="activity.start_time"><v-icon size="16">mdi-clock-outline</v-icon>{{ activity.start_time }}<template v-if="activity.end_time"> – {{ activity.end_time }}</template></span>
                    <span v-if="activity.city"><v-icon size="16">mdi-map-marker-outline</v-icon>{{ activity.city }}</span>
                    <span v-if="activity.duration"><v-icon size="16">mdi-timer-outline</v-icon>{{ activity.duration }}</span>
                    <span v-if="activity.type"><v-icon size="16">mdi-tag-outline</v-icon>{{ activity.type }}</span>
                    <span v-if="activity.price_per_person"><v-icon size="16">mdi-cash</v-icon>{{ money(activity.price_per_person) }} / adult</span>
                  </div>
                </div>
              </div>
              <p v-if="!details.activities.length" class="tn-muted mt-4 mb-0">No activity was saved with this booking.</p>

              <div v-if="hasPickup" class="tn-subsection">
                <div class="tn-subsection__title">Pickup</div>
                <div class="tn-contact mt-1">
                  <span v-if="details.pickup.hotel_name"><v-icon size="18">mdi-office-building-marker-outline</v-icon>{{ details.pickup.hotel_name }}</span>
                  <span v-if="details.pickup.room_number"><v-icon size="18">mdi-door</v-icon>Room {{ details.pickup.room_number }}</span>
                </div>
              </div>
            </div>
          </v-card>

          <travellers-panel :travellers="travellers" />
        </div>

        <div class="tn-booking__col">
          <customer-panel
            :customer="customer"
            :contact="contact"
            :empty-text="hasOverview ? 'No customer account is linked to this booking (guest checkout).' : 'Available after the API update.'"
          />
          <payment-panel :payment="payment" :amount="booking.total_price" />
          <references-panel :items="references" />
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import BookingSummary from '../../components/booking/BookingSummary.vue'
import CustomerPanel from '../../components/booking/CustomerPanel.vue'
import PaymentPanel from '../../components/booking/PaymentPanel.vue'
import ReferencesPanel from '../../components/booking/ReferencesPanel.vue'
import TravellersPanel from '../../components/booking/TravellersPanel.vue'
import bookingPage from '../../mixins/bookingPage'

// What an API without `activity_details` (not deployed yet) already returns.
function fallbackDetails (b) {
  const activities = []
  ;(b.booking_city || []).forEach(city => {
    (city.booking_city_activity || []).forEach(row => {
      const a = row.package_activity
      activities.push({
        id: row.package_activity_id,
        deleted: !a,
        title: a ? a.title : null,
        image: a ? a.image : null,
        type: a ? a.activity_type : null,
        city: city.city ? city.city.name : null,
        date: row.date,
        start_time: a && a.start_time ? String(a.start_time).slice(0, 5) : null,
        end_time: a && a.end_time ? String(a.end_time).slice(0, 5) : null,
        duration: a && a.duration_digits ? `${a.duration_digits} ${a.duration_type || ''}`.trim() : null,
        price_per_person: null
      })
    })
  })
  const dates = activities.map(a => a.date).filter(Boolean).sort()
  return { activities, tour_date: b.start_date || dates[0] || null, last_date: dates[dates.length - 1] || null, price_per_person: null, pickup: {} }
}

export default {
  components: { BookingSummary, CustomerPanel, PaymentPanel, ReferencesPanel, TravellersPanel },
  mixins: [bookingPage],
  computed: {
    details () {
      return this.booking.activity_details || fallbackDetails(this.booking)
    },
    title () {
      const list = this.details.activities
      const first = (list[0] && list[0].title) || (this.hasOverview && this.booking.overview.title) || 'Adventure booking'
      return list.length > 1 ? `${first} + ${list.length - 1} more` : first
    },
    tags () {
      return [this.details.tour_date ? `Tour ${this.date(this.details.tour_date)}` : null]
    },
    tourDateLabel () {
      const { tour_date: first, last_date: last } = this.details
      if (!first) return '—'
      return last && last !== first ? `${this.date(first)} → ${this.date(last)}` : `${this.weekday(first)}, ${this.date(first)}`
    },
    hasPickup () {
      const p = this.details.pickup || {}
      return !!(p.hotel_name || p.room_number)
    },
    references () {
      return [
        { label: 'Booking ID', value: String(this.booking.id) },
        { label: 'Activity IDs', value: this.details.activities.map(a => a.id).filter(Boolean).join(', ') || this.booking.model_ids }
      ]
    }
  }
}
</script>
